import React from 'react';

import { Card, FormItem, Toggle } from '../../../components';
import { Input } from '../../../components/Input';
import { useForm } from '../../../components/Form';
import type { AgentForm as AgentFormType } from '../../../utilities';
import { VoicemailDetectionPatternsEditor } from './VoicemailDetectionPatternsEditor/index';

const VOICEMAIL_DEFAULTS = {
  voicemail_detection_max_listen_seconds: 20,
  voicemail_detection_max_words: 30,
} as const;

export type VoicemailDetectionSettingsProps = {
  disabled?: boolean;
  form: AgentFormType;
};

export const VoicemailDetectionSettings: React.FC<
  VoicemailDetectionSettingsProps
> = ({ disabled, form }) => {
  const { getValue, onChange } = useForm<AgentFormType>();

  const handleEnabledChange = (enabled: boolean) => {
    if (!enabled) {
      const current = getValue?.();
      if (current) {
        onChange?.({ ...current, ...VOICEMAIL_DEFAULTS });
      }
    }
  };

  return (
    <Card
      collapsable
      size="unset"
      title="VoiceMail keyword detection config"
      name="agentpage.voicemailDetection"
      contentClassName="flex flex-col gap-y-md"
      subtitle="Configure voicemail detection behavior during speech recognition"
      withOverflow={false}
    >
      <FormItem<AgentFormType>
        name="voicemail_detection_enabled"
        label="Enable"
        className="flex justify-between items-center"
      >
        <Toggle disabled={disabled} onChange={handleEnabledChange} />
      </FormItem>
      {form.voicemail_detection_enabled && (
        <>
          <FormItem<AgentFormType>
            name="voicemail_detection_max_listen_seconds"
            label="Max STT Transcribe seconds"
            tooltip="How long the agent stays silent listening for a voicemail greeting after the first transcribed callee word arrives. Ring time and silence before pickup do not count toward this window."
          >
            <Input numeric disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="voicemail_detection_max_words"
            label="Max Words"
            tooltip="Total words the callee has to speak before the detector gives up trying to recognize a voicemail and treats the call as a real human. Counted cumulatively across all text since the session started."
          >
            <Input numeric disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="voicemail_detection_max_end_of_speech_events"
            label="Max End Of speech Events Count"
            tooltip="Counts pauses in speech, when specified - all other voicemail detector settings such as Max Seconds or Max words are ignored"
          >
            <Input numeric min={0} roundTo={0} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="voicemail_detection_patterns"
            label="Language Patterns"
            tooltip="Per-language regex patterns used for voicemail detection. Each language can have multiple tiers (tier_1, tier_2, …) with higher-priority patterns listed first. Patterns are standard regex strings."
          >
            <VoicemailDetectionPatternsEditor disabled={disabled} />
          </FormItem>
        </>
      )}
    </Card>
  );
};
